import { useCallback, useEffect, useRef, useState } from 'react';
import { templatesApi, type Template } from '../../api/templates';
import type { AutosaveState } from './AutosaveIndicator';

interface TemplateState {
  template: Template | null;
  loading: boolean;
  error: string | null;
  saveState: AutosaveState;
}

/**
 * Loads a template and exposes a `run` helper that wraps any mutation call in
 * optimistic + autosave semantics. Every mutation returns the full tree, so we
 * just replace state on success and flip saveState.
 *
 * Pass a `templateId` to load a specific template (used by the detail page).
 * Omit it to load the user's default template (used by the legacy consumers
 * that haven't been migrated to the list/detail split).
 */
export function useTemplate(templateId?: string) {
  const [state, setState] = useState<TemplateState>({
    template: null,
    loading: true,
    error: null,
    saveState: 'idle',
  });

  const pending = useRef(0);
  const savedTimeout = useRef<number | null>(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const template = templateId
        ? await templatesApi.getById(templateId)
        : await templatesApi.getMine();
      setState({ template, loading: false, error: null, saveState: 'idle' });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: (e as Error).message ?? 'Failed to load template' }));
    }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async <T extends Template | void>(
    op: () => Promise<T>,
    optimistic?: (prev: Template) => Template,
  ): Promise<Template | null> => {
    pending.current += 1;
    setState((s) => ({
      ...s,
      saveState: 'saving',
      template: s.template && optimistic ? optimistic(s.template) : s.template,
    }));
    if (savedTimeout.current) window.clearTimeout(savedTimeout.current);
    try {
      const result = await op();
      const tree = (result as Template) ?? null;
      pending.current = Math.max(0, pending.current - 1);
      setState((s) => ({
        ...s,
        template: tree ?? s.template,
        saveState: pending.current === 0 ? 'saved' : 'saving',
        error: null,
      }));
      if (pending.current === 0) {
        savedTimeout.current = window.setTimeout(
          () => setState((s) => (s.saveState === 'saved' ? { ...s, saveState: 'idle' } : s)),
          2000,
        );
      }
      return tree;
    } catch (e) {
      pending.current = Math.max(0, pending.current - 1);
      setState((s) => ({ ...s, saveState: 'error', error: (e as Error).message ?? 'Save failed' }));
      // Refresh from server to discard the optimistic patch.
      load();
      return null;
    }
  }, [load]);

  return { ...state, reload: load, run };
}
